import * as $ from 'jquery';
import { Observable } from 'common/barrels/rx';
import { compositeSubscription, disposableSubscription, Lazy } from 'common/barrels/core';
import { Map } from 'immutable';
import { format } from 'common/functions/string';
import { newGuid } from 'common/functions/guid';

import { ContainerControl } from 'runtime/controls/containercontrol';
import { ControlArgs } from 'runtime/controls/controlargs';
import { windowResized } from 'common/rxui/core';
import { MethodRouter } from 'runtime/methodrouter';
import { readBinding, applyBindings, registerBinding } from 'runtime/bindingengine';
import { ViewInfoRequested } from 'runtime/events/viewinforequested';
import { DataRequestStarted } from 'runtime/events/datarequeststarted';
import { DataRequestFinished } from 'runtime/events/datarequestfinished';
import { FileUploadStarted } from 'runtime/events/fileuploadstarted';
import { FileUploadFinished } from 'runtime/events/fileuploadfinished';
import { FileUploadPending } from 'runtime/events/fileuploadpending';
import { SaveRequested } from 'runtime/events/saverequested';
import { SaveCloneRequested } from 'runtime/events/saveclonerequested';
import { SubmitRequested } from 'runtime/events/submitrequested';
import { SubmitCloneRequested } from 'runtime/events/submitclonerequested';
import { ValidateRequested } from 'runtime/events/validaterequested';
import { CloseRequested } from 'runtime/events/closerequested';
import { CancelRequested } from 'runtime/events/cancelrequested';
import { RefreshRequested } from 'runtime/events/refreshrequested';
import { CloneRequested } from 'runtime/events/clonerequested';
import { DialogRequested } from 'runtime/events/dialogrequested';
import { Busy } from 'runtime/events/busy';
import { NotBusy } from 'runtime/events/notbusy';
import { RuntimeErrorOccurred } from 'runtime/events/runtimeerroroccurred';
import { GenericEvent } from 'runtime/events/genericevent';
import { ViewRequested } from 'runtime/events/viewrequested';

import { GenericInfoDialog } from '../dialogs/genericinfodialog';
import { RelinquishClaimDialog } from 'runtime/dialogs/relinquishclaimdialog';

import 'runtime/styles/controls/rootcontainer.less';

export class RootContainer extends ContainerControl {

    private viewInfo: Lazy<ViewInfoRequested> = new Lazy(() => this.eventAggregator.publish(new ViewInfoRequested()));

    private popups = Map<string, JQuery>();

    private savedSinceOpening = false;

    private dialogParentSelectorOverride: string;

    constructor(args: ControlArgs) {
        super(args);
        this.methods.register('Document', this);

        this.$element
            .addClass('rootcontainer')
            .on('dragover', e => e.preventDefault())
            .on('drop', e => e.preventDefault())
            .applyBackground(this.config['content.Background']);

        if(this.config.isScrollable) {
            this.$element.toggleClass('rootcontainer-scrollable');
            this.$element.off('scroll');
        }
        else if(this.$element.hasClass('rootcontainer-popup')) {
            this.$element.toggleClass('rootcontainer-popup-hidden');
        }

        this.bindElements(this.$element.children(), this);

        const earlySubscriptions = compositeSubscription(
            this.eventAggregator.query(DataRequestStarted).filter(() => this.topmost()).subscribe(() => this.triggerEvent('Data requests started')),
            this.eventAggregator.query(DataRequestFinished).filter(() => this.topmost()).subscribe(() => this.triggerEvent('Data requests finished')),
            this.eventAggregator.query(FileUploadStarted).filter(() => this.topmost()).subscribe(() => this.triggerEvent('File uploads started')),
            this.eventAggregator.query(FileUploadFinished).filter(() => this.topmost()).subscribe(() => this.triggerEvent('File uploads finished')),
            this.eventAggregator.query(FileUploadPending).filter(() => this.topmost()).subscribe(() => this.handlePendingUploads())
        );

        // crude, but effective
        this.parentEnabled = true;
        this.config.isElementEnabled = true;
        this.config.isElementVisible = true;
        this.propagateEnabledStateToChildElements(this.$element, true);

        this.initialize();

        this.triggerEvent('Loaded');
        this.subscriptions = disposableSubscription(
            earlySubscriptions,
            this.activatedStream.filter(x => x).subscribe(() => this.triggerEvent('Activated'))
        );
    }

    dispose() {
        this.subscriptions.dispose();
        this.popups.valueSeq().toArray().forEach(x => x.remove());
    }

    save(dummy: any, done: () => void) {
        this.eventAggregator.publish(new SaveRequested(this.methods, () => {
            this.triggerEvent('Saved');
            this.savedSinceOpening = true;
            done();
        }));
    }

    saveClone() {
        this.eventAggregator.publish(new SaveCloneRequested(this.methods, () => this.triggerEvent('Saved')));
    }

    submit() {
        this.eventAggregator.publish(new SubmitRequested(this, this.methods, () => this.triggerEvent('Submitted')));
    }

    submitClone() {
        this.eventAggregator.publish(new SubmitCloneRequested(this, this.methods,() => this.triggerEvent('Submitted')));
    }

    validate() { this.eventAggregator.publish(new ValidateRequested(this)); }

    close() {
        const closeAction = () => this.eventAggregator.publish(new CloseRequested());

        if (!this.$element.hasClass('rootcontainer-popup')) {
            if (this.$element.parents('.rootcontainer-popup').length > 0)
                this.$element.parents('.rootcontainer-popup').first().dialog('close');
            else if (this.values.autoClaimed() && !this.savedSinceOpening)
                this.eventAggregator.publish(new DialogRequested(new RelinquishClaimDialog(this.resources, () => this.cancel(), () => closeAction())));
            else 
                closeAction();
        }
        else 
            this.$element.dialog('close');
    }

    cancel() {
        this.triggerEvent('Cancelled');

        this.eventAggregator.publish(new CancelRequested());
    }

    validated() { this.$element.triggerHandler('events.Validated'); }

    refresh(dummy: any, done: () => void) { this.eventAggregator.publish(new RefreshRequested(done)) }

    clone() {
        this.eventAggregator.publish(new CloneRequested());
    }

    'set Busy'() { this.eventAggregator.publish(new Busy()); }

    'set Not Busy'() { this.eventAggregator.publish(new NotBusy()); }

    fileTypeNotAllowed(allowedTypes?: string[]) {
        if (this.hasSubscriptions('fileTypeNotAllowed'))
            this.triggerEvent('FileTypeNotAllowed');
        else {
            var dialogText = allowedTypes != null && allowedTypes.length > 0
                ? format(this.resources.getCoreString('FileTypeNotSupportedDialogTextWithAllowedTypes'), this.resources.makeList(allowedTypes))
                : this.resources.getCoreString('FileTypeNotSupportedDialogText');

            this.eventAggregator.publish(new DialogRequested(new GenericInfoDialog(this.resources.getCoreString('FileTypeNotSupportedDialogTitle'), dialogText)));
        }
    }

    maxFileSizeExceeded() {
        if (this.hasSubscriptions('maxFileSizeExceeded'))
            this.triggerEvent('MaxFileSizeExceeded');
        else
            this.eventAggregator.publish(new DialogRequested(new GenericInfoDialog(this.resources.getCoreString('MaxFileSizeExceededDialogTitle'), this.resources.getCoreString('MaxFileSizeExceededDialogText'))));
    }

    cameraCaptureNotAvailable() {
        if (this.hasSubscriptions('CameraCaptureNotAvailable'))
            this.triggerEvent('CameraCaptureNotAvailable');
        else
            this.eventAggregator.publish(new DialogRequested(new GenericInfoDialog(this.resources.getCoreString('CameraCaptureNotAvailableTitle'), this.resources.getCoreString('CameraCaptureNotAvailableText'))));
    }

    cameraCaptureError() {
        if (this.hasSubscriptions('CameraCaptureError'))
            this.triggerEvent('CameraCaptureError');
        else
            this.eventAggregator.publish(new DialogRequested(new GenericInfoDialog(this.resources.getCoreString('CameraCaptureErrorTitle'), this.resources.getCoreString('CameraCaptureErrorText'))));
    }

    private handlePendingUploads() {
        if (this.hasSubscriptions('File uploads pending'))
            this.triggerEvent('File uploads pending')
        else
            this.eventAggregator.publish(new DialogRequested(new GenericInfoDialog(this.resources.getCoreString('FileUploadPendingDialogTitle'), this.resources.getCoreString('FileUploadPendingDialogMessage'))));
    }

    'Runtime error occurred'() {
        if (this.hasSubscriptions('Runtime error occurred'))
            this.triggerEvent('Runtime error occurred');
        else
            this.eventAggregator.publish(new RuntimeErrorOccurred());
    }

    'show Popup'(args) {
        var viewName = args.viewName || args;
        var fieldContext = args.fieldContext || this.fieldContext;

        this.eventAggregator.publish(new ViewRequested(viewName, view => {
            var $view = $(view).addClass('rootcontainer-popup').appendTo(this.$element);
            var config = readBinding($view, 'rootcontainer');
            var popupKey = newGuid();
            var languageKey = config['content.LanguageKey'];

            const title = languageKey ? this.resources.getString(this.viewInfo.value().definitionName, languageKey) : Observable.of<string>(config['content.Title']);

            const subscription = title.subscribe(titleValue => {
                // the magic-number-using height calculation below is to get the sizes of the dialog to exactly match up with the silverlight equivalents; not sure if this can be done in pure styles
                $view.dialog({
                    appendTo: this.dialogParentSelectorOverride || '.runtimehost',
                    modal: true,
                    minWidth: Math.min(config.elementWidth, $('body').width() - 6),
                    minHeight: Math.min(config.elementHeight + 8, $('body').height()),
                    title: titleValue,
                    close: () => {
                        this.popups = this.popups.remove(popupKey);
                        $view.remove();
                        this.eventAggregator.publish(new GenericEvent(format('{0}_Closed', viewName), ''));
                    },
                    open: () => this.installScrollVeto($view.parent())
                });

                this.popups = this.popups.set(popupKey, $view);
                const methods = new MethodRouter(this.methods, viewName);
                $view.owns(methods);
                applyBindings($view, fieldContext, this.eventAggregator, methods, this.values, this.resources, this, this.serializer);
                $view.triggerHandler('activated');
                this.setFocusToFirstFocusableSubElement($view);
            });

            $view.owns(subscription);
        }));
    }

    applyEnabledState(value: boolean) {
        this.propagateEnabledStateToChildElements(this.$element, this.writableAgnosticDerivedEnabledState())
    }

    activate() {
        this.activatedSubject.next(true);
    }

    deactivate() {
        this.activatedSubject.next(false);
    }

    configuredHeight() {
        return this.config.elementHeight;
    }

    configuredWidth() {
        return this.config.elementWidth;
    }

    resizeStream(): Observable<{}> {
        return windowResized();
    }

    private topmost(): boolean {
        return this.popups.count() === 0;
    }

    setPopupHostOverride(selector: string) {
        this.dialogParentSelectorOverride = selector;
    }
}

registerBinding('rootcontainer', RootContainer);